/**
 * iter-106 — Per-dashboard auto-refresh + manual refresh control.
 *
 * Renders a compact split button: left side triggers an immediate refresh
 * (broadcasts the `dashboard:refresh` window event so LockedKpiStrip's
 * refreshKey advances); right side opens a dropdown to set the cadence.
 *
 * Cadence is persisted via PUT /api/schedules/dashboard/{id} so it survives
 * sessions, tabs, and devices. The local timer is bound to dashboard.id so
 * switching dashboards stops the previous timer cleanly.
 */
import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { RefreshCw, Clock, ChevronDown, Lock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { isPaused as schedulesArePaused } from "@/lib/schedules-pause";
import { useEntitlements } from "@/lib/use-entitlements";

const PRESETS = [
    { secs: 0, label: "Off" },
    { secs: 30, label: "Every 30s" },
    { secs: 60, label: "Every 1 min" },
    { secs: 300, label: "Every 5 min" },
    { secs: 900, label: "Every 15 min" },
    { secs: 1800, label: "Every 30 min" },
    { secs: 3600, label: "Every 1 hour" },
];

export function DashboardScheduleControl({ dashboard, onChanged }) {
    const [secs, setSecs] = useState(dashboard?.auto_refresh_seconds || 0);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);
    const tickRef = useRef(null);
    const { entitled, limitReached, openUpgrade, role } = useEntitlements();
    // Plan gate — scheduled auto-refresh requires the `report_scheduling`
    // feature AND an available `max_schedules` slot. SA never locked.
    const scheduleLocked = role !== "super_admin"
        && secs === 0
        && (!entitled("report_scheduling") || limitReached("max_schedules"));

    useEffect(() => {
        // sync state when the user opens a different dashboard
        setSecs(dashboard?.auto_refresh_seconds || 0);
    }, [dashboard?.id, dashboard?.auto_refresh_seconds]);

    useEffect(() => {
        // (re)arm the auto-refresh timer scoped to this dashboard
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
        if (secs > 0 && dashboard?.id) {
            tickRef.current = setInterval(() => {
                // iter-108: respect tenant-wide emergency pause set from /schedules
                if (schedulesArePaused()) return;
                window.dispatchEvent(new CustomEvent("dashboard:refresh", {
                    detail: { id: dashboard.id, source: "auto" },
                }));
            }, secs * 1000);
        }
        return () => {
            if (tickRef.current) clearInterval(tickRef.current);
        };
    }, [secs, dashboard?.id]);

    const setCadence = async (next) => {
        if (next === secs) return;
        setSaving(true);
        try {
            const r = await api.put(`/schedules/dashboard/${dashboard.id}`, { seconds: next });
            setSecs(next);
            onChanged?.(next);
            toast.success(next === 0 ? "Auto-refresh off" : `Auto-refresh set to ${r.data?.label}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update schedule");
        } finally {
            setSaving(false);
        }
    };

    const refreshNow = () => {
        setBusy(true);
        window.dispatchEvent(new CustomEvent("dashboard:refresh", {
            detail: { id: dashboard.id, source: "manual" },
        }));
        // brief visual feedback; LockedKpiStrip handles the actual fetch
        setTimeout(() => setBusy(false), 500);
    };

    const currentLabel = PRESETS.find((p) => p.secs === secs)?.label || `Every ${secs}s`;
    return (
        <div className="inline-flex items-center" data-testid="dashboard-schedule-control">
            <Button
                variant="outline"
                className="rounded-sm rounded-r-none border-r-0 px-3"
                onClick={refreshNow}
                disabled={busy}
                title="Refresh now"
                data-testid="dashboard-refresh-now"
            >
                <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
                Refresh
            </Button>
            {scheduleLocked ? (
                <Button
                    variant="outline"
                    className="rounded-sm rounded-l-none px-2"
                    disabled={saving}
                    onClick={() => openUpgrade()}
                    title="Scheduled auto-refresh isn't included in your plan — upgrade to unlock"
                    data-testid="dashboard-schedule-menu-trigger"
                >
                    <Lock className="h-3.5 w-3.5" />
                    <ChevronDown className="h-3 w-3 ml-0.5" />
                </Button>
            ) : (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        className={`rounded-sm rounded-l-none px-2 ${secs > 0 ? "border-primary/40 text-primary" : ""}`}
                        disabled={saving}
                        title={secs > 0 ? `Auto-refresh: ${currentLabel}` : "Set auto-refresh"}
                        data-testid="dashboard-schedule-menu-trigger"
                    >
                        <Clock className="h-3.5 w-3.5" />
                        <ChevronDown className="h-3 w-3 ml-0.5" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px]" data-testid="dashboard-schedule-menu">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                        Auto-refresh cadence
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {PRESETS.map((p) => (
                        <DropdownMenuItem
                            key={p.secs}
                            onClick={() => setCadence(p.secs)}
                            className={`text-xs ${p.secs === secs ? "bg-primary/10 text-primary font-medium" : ""}`}
                            data-testid={`dashboard-schedule-${p.secs}`}
                        >
                            {p.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            )}
        </div>
    );
}

export default DashboardScheduleControl;
